import { Button } from '@frontdoor/ui/components/button';

import type { TrackingVerifyPayload } from '@/api/generated';
import { NeedHelpFooter } from '@/components/layout/NeedHelpFooter.tsx';
import { OrderDetailsCard } from '@/components/tracking/OrderDetailsCard.tsx';
import { OrderSummaryCard } from '@/components/tracking/OrderSummaryCard.tsx';
import { ShipmentsCard } from '@/components/tracking/ShipmentsCard.tsx';
import { StatusBanner } from '@/components/tracking/StatusBanner.tsx';
import { VisitDetailsCard } from '@/components/tracking/VisitDetailsCard.tsx';

import { resolveStatusView } from './status-config.tsx';

type StatusPageProps = {
  data: TrackingVerifyPayload;
  publicId: string;
};

export function StatusPage({ data }: StatusPageProps) {
  const view = resolveStatusView(data);
  const isErrorState = data.status === 'error';

  if (isErrorState) {
    return (
      <div className="mx-auto flex w-full max-w-[1066px] flex-1 flex-col px-4 md:px-7 lg:max-w-[1090px] lg:px-10">
        <div className="flex flex-1 flex-col items-center justify-center gap-6 py-8 text-center">
          <img src="/icons/chain-broken.svg" alt="" className="size-[60px]" />
          <div>
            <h1 className="text-xl leading-tight font-medium">{view.title}</h1>
            <p className="mt-2 text-muted-foreground">{view.description}</p>
          </div>
          <Button className="mt-2 w-full max-w-md" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </div>
        <NeedHelpFooter />
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1066px] flex-1 flex-col px-4 md:px-7 lg:max-w-[1090px] lg:px-10">
      <div className="md:grid md:grid-cols-[2fr_1fr] md:gap-8 lg:gap-12">
        {/* Left column */}
        <div className="flex flex-col gap-6">
          <StatusBanner view={view} />

          {view.showVisitDetails && data.order.telehealth && (
            <VisitDetailsCard telehealth={data.order.telehealth} data={data} />
          )}

          {data.order.shipments && data.order.shipments.length > 0 && (
            <ShipmentsCard shipments={data.order.shipments} />
          )}

          {data.order.items && data.order.items.length > 0 && (
            <OrderSummaryCard items={data.order.items} />
          )}
        </div>

        {/* Right column */}
        <div className="mt-6 md:mt-0">
          <div className="md:pt-8">
            <OrderDetailsCard order={data.order} />
          </div>

          {/* NeedHelpFooter: shown in right column on desktop */}
          <div className="hidden md:block">
            <NeedHelpFooter />
          </div>
        </div>
      </div>

      {/* NeedHelpFooter: full width on mobile */}
      <div className="mt-auto md:hidden">
        <NeedHelpFooter />
      </div>
    </div>
  );
}
